<template>
  <div>
    <v-alert v-if="!usuarioLogado.adminWeb" type="warning" outlined dense class="mb-4">
      Acesso restrito: faça login como administrador para consultar respostas por instituição.
    </v-alert>
    <v-container v-else fluid class="respostas-container">
      <v-card class="pa-5 mb-6" elevation="2">
        <v-card-title class="font-weight-bold">Respostas por Instituição</v-card-title>
        <v-row align="center">
          <v-col cols="12" md="2">
            <v-text-field v-model.number="ano" label="Ano" type="number" outlined dense hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="5">
            <v-select
              v-model="idInstrumento"
              :items="instrumentos"
              item-text="descricao"
              item-value="id"
              label="Instrumento"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="5">
            <v-autocomplete
              v-model="idQuestionario"
              :items="questionarios"
              item-text="anexo"
              item-value="id"
              label="Questionário"
              :disabled="!idInstrumento"
              outlined
              dense
              hide-details
            >
              <template v-slot:selection="{ item }">{{ rotuloQuestionario(item) }}</template>
              <template v-slot:item="{ item }">{{ rotuloQuestionario(item) }}</template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-select
              v-model="idInstituicao"
              :items="escolas"
              item-text="descricao"
              item-value="pkescola"
              label="Instituição"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn color="primary" block :loading="loading" :disabled="!idQuestionario || !idInstituicao" @click="buscar">
              <v-icon left>mdi-magnify</v-icon>
              Buscar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card elevation="2" class="pa-4">
        <v-card-title class="font-weight-bold d-flex align-center px-0">
          Respostas
          <v-spacer />
          <v-btn color="primary" small :disabled="!questoesAgrupadas.length" @click="exportarPdf">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Exportar PDF
          </v-btn>
        </v-card-title>

        <template v-if="questoesAgrupadas.length">
          <v-card v-for="grupo in questoesAgrupadas" :key="`${grupo.ordem}-${grupo.questao}`" outlined class="mb-4 questao-card">
            <v-card-title class="questao-titulo">
              <v-avatar color="primary" size="28" class="mr-3">
                <span class="white--text caption font-weight-bold">{{ grupo.ordem }}</span>
              </v-avatar>
              <span class="body-1 font-weight-medium">{{ grupo.questao }}</span>
            </v-card-title>
            <v-card-text>
              <div v-for="(r, idx) in grupo.respostas" :key="idx" class="mb-3">
                <div class="d-flex justify-space-between mb-1">
                  <span class="body-2">{{ r.texto }}</span>
                  <span class="body-2 font-weight-medium">{{ r.quantidade }} ({{ r.percentual.toFixed(1) }}%)</span>
                </div>
                <v-progress-linear :value="r.percentual" height="14" rounded :color="corBarra(idx)"></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </template>

        <v-card-text v-else-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="buscaRealizada" class="text-center text--secondary pa-8">
          Nenhuma resposta encontrada para essa combinação.
        </v-card-text>
        <v-card-text v-else class="text-center text--secondary pa-8">
          Selecione o questionário e a instituição e clique em Buscar.
        </v-card-text>
      </v-card>
    </v-container>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { jsPDF } from "jspdf";
import { Libs } from "@/libs/libs";
import EscolaServices from "@/services/base/EscolaServices";
import InstrumentoQuestionarioServices from "@/services/rh/InstrumentoQuestionarioServices";
import RespostasServices from "@/services/ava/Respostas.services";

export default {
  name: "RespostasPorInstituicao",
  data: () => ({
    loading: false,
    buscaRealizada: false,
    ano: new Date().getFullYear(),
    idInstrumento: null,
    escolas: [],
    respostas: [],
    idQuestionario: null,
    idInstituicao: null,
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",
  }),
  computed: {
    ...mapGetters({
      usuarioLogado: "config/getUsuarioLogado",
      instrumentos: "avaliacao/getListaInstrumentos",
      questionarios: "avaliacao/getListaQuestionariosInstrumento",
    }),
    questoesAgrupadas() {
      if (!this.respostas.length) return [];
      const chaveQuestao = this.detectarChave(["questao", "descricao_questao", "pergunta"]);
      const chaveOrdemQuestao = this.detectarChave(["ordem_questao"]);
      const chaveResposta = this.detectarChave(["resposta", "descricao_resposta", "opcao"]);
      const chaveOrdemResposta = this.detectarChave(["ordem"]);
      const chaveQuantidade = this.detectarChave(["respostas", "quantidade", "total", "qtd", "count"]);

      const grupos = {};
      const ordemPorQuestao = [];
      this.respostas.forEach((r) => {
        const questaoTexto = chaveQuestao ? r[chaveQuestao] : "";
        let ordemQuestao = chaveOrdemQuestao ? r[chaveOrdemQuestao] : null;
        if (ordemQuestao === null || ordemQuestao === undefined) {
          let posicao = ordemPorQuestao.indexOf(questaoTexto);
          if (posicao === -1) {
            ordemPorQuestao.push(questaoTexto);
            posicao = ordemPorQuestao.length - 1;
          }
          ordemQuestao = posicao + 1;
        }
        const chaveGrupo = questaoTexto;
        if (!grupos[chaveGrupo]) {
          grupos[chaveGrupo] = { ordem: ordemQuestao, questao: questaoTexto, respostas: [], total: 0 };
        }
        const respostaTexto = chaveResposta ? r[chaveResposta] ?? "Não informado" : "Não informado";
        const ordemResposta = chaveOrdemResposta ? Number(r[chaveOrdemResposta]) || 0 : 0;
        const quantidade = chaveQuantidade ? Number(r[chaveQuantidade]) || 0 : 1;

        let item = grupos[chaveGrupo].respostas.find((x) => x.texto === respostaTexto);
        if (!item) {
          item = { texto: respostaTexto, quantidade: 0, ordem: ordemResposta };
          grupos[chaveGrupo].respostas.push(item);
        }
        item.quantidade += quantidade;
        grupos[chaveGrupo].total += quantidade;
      });

      return Object.values(grupos)
        .sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0))
        .map((g) => ({
          ordem: g.ordem,
          questao: g.questao,
          total: g.total,
          respostas: [...g.respostas]
            .sort((a, b) => a.ordem - b.ordem)
            .map((r) => ({
              texto: r.texto,
              quantidade: r.quantidade,
              percentual: g.total ? (r.quantidade / g.total) * 100 : 0,
            })),
        }));
    },
    questionarioSelecionado() {
      return this.questionarios.find((q) => q.id === this.idQuestionario) || null;
    },
    instituicaoSelecionada() {
      return this.escolas.find((e) => e.pkescola === this.idInstituicao) || null;
    },
  },
  watch: {
    ano() {
      this.idInstrumento = null;
      this.idQuestionario = null;
      this.carregarInstrumentos();
    },
    idInstrumento(valor) {
      this.idQuestionario = null;
      if (valor) this.carregarQuestionariosInstrumento();
    },
  },
  mounted() {
    this.carregarInstrumentos();
    this.carregarEscolas();
  },
  methods: {
    detectarChave(candidatos) {
      if (!this.respostas.length) return null;
      const chaves = Object.keys(this.respostas[0]);
      return candidatos.find((c) => chaves.includes(c)) || null;
    },
    corBarra(idx) {
      const cores = ["primary", "success", "warning", "error", "indigo", "teal", "deep-purple", "brown"];
      return cores[idx % cores.length];
    },
    rotuloQuestionario(item) {
      if (!item || typeof item !== "object") return "";
      const nome = item.anexo || item.nome || item.titulo || `Questionário ${item.id}`;
      const extra = [item.instrumento, item.ano].filter(Boolean).join(" - ");
      return extra ? `${nome} (${extra})` : nome;
    },
    async carregarInstrumentos() {
      try {
        await InstrumentoQuestionarioServices.LoadLista({ anos: this.ano });
      } catch (error) {
        this.exibirMensagem("Erro ao carregar instrumentos.", "error");
      }
    },
    async carregarQuestionariosInstrumento() {
      try {
        await InstrumentoQuestionarioServices.loadQuestionariosInstrumento(this.ano, this.idInstrumento);
      } catch (error) {
        this.exibirMensagem("Erro ao carregar questionários do instrumento.", "error");
      }
    },
    async carregarEscolas() {
      try {
        const data = await EscolaServices.load({ fkadministracao: 1 });
        this.escolas = data || [];
      } catch (error) {
        this.exibirMensagem("Erro ao carregar instituições.", "error");
      }
    },
    async buscar() {
      this.loading = true;
      this.buscaRealizada = false;
      try {
        const resp = await RespostasServices.porInstituicao(this.idInstituicao, this.idQuestionario);
        this.respostas = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : [];
      } catch (error) {
        this.respostas = [];
        this.exibirMensagem(error?.response?.data?.error || "Erro ao buscar respostas.", "error");
      } finally {
        this.loading = false;
        this.buscaRealizada = true;
      }
    },
    exportarPdf() {
      if (!this.questoesAgrupadas.length) return;
      const nomeQuestionario = this.rotuloQuestionario(this.questionarioSelecionado);
      const nomeInstituicao = this.instituicaoSelecionada ? this.instituicaoSelecionada.descricao : "";

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const dataAtual = new Date().toLocaleString("pt-BR");
      const margem = 40;
      const larguraPagina = doc.internal.pageSize.getWidth();
      const alturaPagina = doc.internal.pageSize.getHeight();
      const larguraBarra = larguraPagina - margem * 2;
      const coresRgb = [
        [25, 118, 210],
        [67, 160, 71],
        [251, 140, 0],
        [211, 47, 47],
        [94, 53, 177],
        [0, 121, 107],
        [93, 64, 55],
        [69, 90, 100],
      ];
      let y = margem;

      doc.setFontSize(14);
      doc.text("Respostas por Instituição", margem, y);
      y += 18;
      doc.setFontSize(10);
      doc.text(`${nomeQuestionario} - ${nomeInstituicao}`, margem, y);
      y += 14;
      doc.text(`Emitido em ${dataAtual}`, margem, y);
      y += 24;

      this.questoesAgrupadas.forEach((grupo) => {
        if (y > alturaPagina - 100) {
          doc.addPage();
          y = margem;
        }
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        const linhas = doc.splitTextToSize(`${grupo.ordem ? grupo.ordem + ". " : ""}${grupo.questao}`, larguraBarra);
        doc.text(linhas, margem, y);
        doc.setFont(undefined, "normal");
        y += linhas.length * 14 + 8;

        grupo.respostas.forEach((r, idx) => {
          if (y > alturaPagina - 60) {
            doc.addPage();
            y = margem;
          }
          doc.setFontSize(9);
          doc.text(`${r.texto} - ${r.quantidade} (${r.percentual.toFixed(1)}%)`, margem, y);
          y += 10;

          const cor = coresRgb[idx % coresRgb.length];
          doc.setFillColor(230, 230, 230);
          doc.rect(margem, y, larguraBarra, 8, "F");
          doc.setFillColor(cor[0], cor[1], cor[2]);
          doc.rect(margem, y, larguraBarra * (r.percentual / 100), 8, "F");
          y += 20;
        });
        y += 8;
      });

      const arquivo = `Respostas_${Libs.limparNomeArquivo(nomeQuestionario)}_${Libs.limparNomeArquivo(nomeInstituicao)}.pdf`;
      doc.save(arquivo);
    },
    exibirMensagem(texto, cor) {
      this.snackbarText = texto;
      this.snackbarColor = cor;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.respostas-container {
  padding: 16px;
}

.questao-card {
  border-radius: 10px !important;
}

.questao-titulo {
  align-items: flex-start;
}

@media (min-width: 1264px) {
  .respostas-container {
    padding-left: 40px;
    padding-right: 40px;
  }
}
</style>
